import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { jumpTo } from '../lib/jump.js';
import type {
  LeakageItem,
  LeakageReport,
  PLReport,
  ProfitRow,
  Summary,
} from '../lib/types.js';
import { fmtInt, fmtMoney, fmtPct, mmdd, rangeFor } from '../lib/format.js';
import { chip, pointsChip, type Chip } from '../lib/delta.js';
import { monetizedSub, requestsSub, revenueSub, searchesSub } from '../lib/subs.js';
import { accountOption, sourceKeyName, sourceName } from '../lib/source-name.js';
import { filterFeedRows, filterLeakage, geoFilterNote } from '../lib/page-filters.js';
import { hiddenColumnsNote, visibleColumns } from '../lib/visibleColumns.js';
import { costTileView, coverageLabel, heroView } from '../lib/pl-view.js';
import { errorMessage } from './settings/common.js';
import { leakageAction, leakageTag, leakageTile } from '../lib/costs-view.js';
import { BarChart, type BarDatum } from '../components/BarChart.js';
import { Modal } from '../components/ui/Modal.js';
import { FilterSelect } from '../components/FilterSelect.js';
import { RANGES } from '../lib/ranges.js';
import { dailySearch } from '../lib/daily-url.js';
import { Excluded, tdClass, type Col } from './table-bits.js';
import { useIsPhone } from '../lib/use-media.js';

const CHART_COLORS = [
  'var(--accent)',
  'var(--blue)',
  'var(--magenta)',
  'var(--gold)',
  'var(--bar-dim)',
];

interface Filters {
  account?: string | undefined;
  rep?: string | undefined;
  provider?: string | undefined;
  feedId?: string | undefined;
  country?: string | undefined;
}

function filterQuery(from: string, to: string, fl: Filters): string {
  const p = new URLSearchParams({ from, to });
  for (const [k, v] of Object.entries(fl)) if (v) p.set(k, v);
  return p.toString();
}

export function DashboardPage({ navigate }: { navigate?: ((to: string) => void) | undefined }) {
  const qc = useQueryClient();
  const [days, setDays] = useState(30);
  const [filters, setFilters] = useState<Filters>({});
  // Costs are always on since the redesign: the Costs tab owns the detail.
  const showCosts = true;
  // "Collapse overview" folds the leakage list and the charts away so the
  // network table sits right under the hero tiles; remembered per browser.
  const [overview, setOverview] = useState<boolean>(() => {
    try {
      return localStorage.getItem('traffic.overview') !== 'closed';
    } catch {
      return true;
    }
  });
  const toggleOverview = () =>
    setOverview((o) => {
      try {
        localStorage.setItem('traffic.overview', o ? 'closed' : 'open');
      } catch {
        /* a per-browser convenience only */
      }
      return !o;
    });
  // The feed whose drill-down is open: "<accountId>:<feedId>".
  const [drill, setDrill] = useState<string | null>(null);
  // Phone (2026-09-14 mobile design): the filters fold behind one chip, the
  // leakage tile unfolds its list in place, the network is cards and the
  // daily table waits behind a button. Same data and rules as the desktop.
  const phone = useIsPhone();
  const [filtersOpen, setFiltersOpen] = useState(false);
  // The row of range chips scrolls sideways on a phone: keep the pressed one in view.
  const segRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!phone) return;
    segRef.current
      ?.querySelector<HTMLElement>('[aria-pressed="true"]')
      ?.scrollIntoView({ inline: 'nearest', block: 'nearest' });
  }, [phone, days]);
  const [leakOpen, setLeakOpen] = useState(false);
  const { from, to } = rangeFor(days);
  const qs = filterQuery(from, to, filters);

  const summary = useQuery({
    queryKey: ['summary', qs],
    queryFn: () => api.get<Summary>(`/api/summary?${qs}`),
    refetchInterval: (q) => (q.state.data?.refreshing ? 4000 : false),
  });
  const profit = useQuery({
    queryKey: ['profit', days],
    queryFn: () => api.get<{ feeds: ProfitRow[] }>(`/api/profit?days=${days}`),
  });
  const pl = useQuery({
    queryKey: ['pl', days],
    queryFn: () => api.get<PLReport>(`/api/pl?days=${days}`),
    enabled: showCosts,
  });
  const leakage = useQuery({
    queryKey: ['leakage', days],
    queryFn: () => api.get<LeakageReport>(`/api/leakage?days=${days}`),
  });
  // The breakdown cards say why they are empty: still loading, failed, or a
  // window with no revenue rows (the local data may simply end before it).
  const bdState: BreakdownState = summary.isPending
    ? 'loading'
    : summary.isError
      ? 'error'
      : 'ready';

  // The Type dropdown on the network table: the choice shows at once (the row
  // used to snap back to the old type until the table refetched, which read as
  // "not working" - owner report 2026-09-18), a save that fails says so, and a
  // save that touched no row is a failure, never a silent no.
  const [typeError, setTypeError] = useState<string | null>(null);
  const [typeOverride, setTypeOverride] = useState<Record<string, string>>({});
  const typeKey = (v: { accountId: string; feedId: string }) => `${v.accountId}:${v.feedId}`;
  const setFeedType = useMutation({
    mutationFn: async (v: { accountId: string; feedId: string; type: string }) => {
      const res = await api.post<{ ok: boolean; updated: number }>(
        `/api/feed-type?feedId=${encodeURIComponent(v.feedId)}&accountId=${encodeURIComponent(v.accountId)}&scope=feed&type=${encodeURIComponent(v.type)}`,
      );
      if (!res.updated)
        throw new Error(
          `no feed ${v.feedId} under ${v.accountId} to classify yet - pull its source first`,
        );
      return res;
    },
    onMutate: (v) => {
      setTypeError(null);
      setTypeOverride((o) => ({ ...o, [typeKey(v)]: v.type }));
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['profit'] });
      void qc.invalidateQueries({ queryKey: ['summary'] });
      void qc.invalidateQueries({ queryKey: ['data'] });
    },
    onError: (e, v) => {
      setTypeError(`Could not save the type of feed ${v.feedId}: ${errorMessage(e)}`);
      setTypeOverride((o) => {
        const next = { ...o };
        delete next[typeKey(v)];
        return next;
      });
    },
  });
  // Fresh table data carries the saved types: the overrides have done their job.
  useEffect(() => {
    setTypeOverride({});
  }, [profit.data]);

  const s = summary.data;
  const failing = s?.accountStatus.filter((a) => a.lastError) ?? [];
  // Credential failures are called out by name: they are the one kind nobody
  // can fix by waiting, and the one the raw message disguised worst.
  const badCreds = failing.filter((a) => a.lastErrorKind === 'credentials');

  // While a pull runs (Configuration → Providers → Run now, or the daily
  // crawl) the summary polls until it lands; the P&L, per-feed profit,
  // buy-side-by-label and daily rows read the same tables and do not refetch
  // on focus, so without this the page showed two revenue totals after a
  // crawl (R30). When `refreshing` flips false they are all stale. The
  // page's own pull selector and "Refresh data" button were retired on
  // 2026-09-14 (owner's call): pulls are run per source under Providers.
  const wasRefreshing = useRef(false);
  const refreshing = s?.refreshing ?? false;
  useEffect(() => {
    if (wasRefreshing.current && !refreshing) {
      for (const key of ['pl', 'profit', 'leakage', 'data', 'attention'])
        void qc.invalidateQueries({ queryKey: [key] });
    }
    wasRefreshing.current = refreshing;
  }, [refreshing, qc]);

  // Provider-keyed surfaces (chart series, breakdowns) show the provider's
  // name; anything with an account in view shows the source's own name.
  // The source axis: a provider's brand, or a pasted sheet's / upload's own name (never "Custom source").
  const accountsById = new Map((s?.accounts ?? []).map((a) => [a.id, a]));
  // The network table and the leakage list are whole-network reads: slice
  // them here under the page filters (lib/page-filters.ts, 2026-09-15).
  const feedRows = filterFeedRows(profit.data?.feeds ?? [], filters).map((f) => {
    const t = typeOverride[typeKey(f)];
    return t === undefined ? f : { ...f, type: t || 'Unclassified' };
  });
  const feedTotal = profit.data?.feeds.length ?? 0;
  const leak = filterLeakage(leakage.data, filters, accountsById);
  const geoNote = geoFilterNote(filters);
  const provDisp = (key: string, label?: string | null) => sourceKeyName(key, label, accountsById);
  const labelById = new Map((s?.accounts ?? []).map((a) => [a.id, a.label]));
  const rowSource = (r: {
    accountId: string;
    provider: string;
    providerLabel?: string | null | undefined;
  }) =>
    sourceName({
      provider: r.provider,
      providerLabel: r.providerLabel,
      label: labelById.get(r.accountId),
    });

  // Stable provider→color assignment for chart + legend.
  const providerColors = useMemo(() => {
    const provs = [
      ...new Set((s?.daily ?? []).flatMap((d) => Object.keys(d.revenueByProvider))),
    ].sort();
    return new Map(provs.map((p, i) => [p, CHART_COLORS[i % CHART_COLORS.length]!]));
  }, [s?.daily]);

  const revenueData: BarDatum[] = (s?.daily ?? []).map((d) => ({
    day: d.day,
    segments: [...providerColors.entries()].map(([p, color]) => ({
      key: p,
      value: d.revenueByProvider[p] ?? 0,
      color,
    })),
    line: showCosts ? d.cost : null,
    tooltip:
      `${mmdd(d.day)}${d.day === s?.partialDay ? ' (partial)' : ''}\n` +
      [...providerColors.keys()]
        .filter((p) => d.revenueByProvider[p])
        .map((p) => `${provDisp(p)}  ${fmtMoney(d.revenueByProvider[p] ?? 0)}`)
        .join('\n') +
      `\nrevenue  ${fmtMoney(d.revenue)}` +
      (d.cost != null
        ? `\ncost  ${fmtMoney(d.cost)}\nprofit  ${fmtMoney(d.revenue - d.cost)}`
        : ''),
  }));

  // Visits the buy side SENT (every Airman destination) over the searches that
  // came back: the gap between the line and the bars is traffic that never
  // became a search - the first place leakage shows. Volume, never money.
  const visitsData: BarDatum[] = (s?.daily ?? []).map((d) => ({
    day: d.day,
    segments: [{ key: 'searches', value: d.searches, color: 'var(--bar-dim)' }],
    line: d.committed,
    tooltip:
      `${mmdd(d.day)}${d.day === s?.partialDay ? ' (partial)' : ''}\nvisits sent  ${fmtInt(d.committed ?? 0)}` +
      `\nsearches  ${fmtInt(d.searches)}\nmonetized  ${fmtInt(d.monetizedSearches)}`,
  }));

  const t = s?.totals;
  const p = s?.prior ?? null;

  const leakRows = (leak?.items ?? []).map((it) => (
    <LeakageRow
      key={`${it.kind}:${it.accountId ?? ''}:${it.feedId ?? ''}:${it.label}`}
      item={it}
      onAct={(item) => {
        if (item.action.kind === 'matching')
          navigate?.(
            item.action.target
              ? `/matching#feed-${encodeURIComponent(item.action.target)}`
              : '/matching',
          );
        else if (item.action.kind === 'sources') navigate?.('/configuration/sources');
        else if (item.action.kind === 'costs') navigate?.('/costs');
        else if (item.feedId && item.accountId) setDrill(`${item.accountId}:${item.feedId}`);
      }}
    />
  ));
  const leakEmpty =
    leak && leak.items.length === 0 ? (
      <div className="table-note">
        Nothing leaking in this window: every confirmed dollar of spend earned something, and every
        crawlable source has stats for every day.
      </div>
    ) : null;
  const activeFilters = (Object.entries(filters) as [keyof Filters, string | undefined][]).filter(
    (e): e is [keyof Filters, string] => !!e[1],
  );
  const filterChipLabel = (k: keyof Filters, v: string): string => {
    if (k === 'feedId') return `Feed ${v}`;
    if (k === 'provider') return provDisp(v);
    if (k === 'account') {
      const a = s?.accounts.find((x) => x.id === v);
      return a ? accountOption(a) : v;
    }
    return v;
  };

  return (
    <div className={showCosts ? '' : 'hide-costs'}>
      <div className="pagehead">
        <h1>Publisher stats</h1>
        <div className="head-right">
          <div className="meta">
            {s?.lastUpdated ? (
              <>Updated {s.lastUpdated.slice(0, 16).replace('T', ' ')}</>
            ) : (
              'no data yet'
            )}
            {s?.refreshing ? (
              <>
                {' '}
                · <span className="spin">⟳</span> pulling…
              </>
            ) : null}
            {failing.length ? (
              <div className="err" title={failing.map((f) => `${f.id}: ${f.lastError}`).join('\n')}>
                {failing.length} account{failing.length > 1 ? 's' : ''} failing
                {badCreds.length
                  ? ` — ${badCreds.length === failing.length ? 'credentials rejected' : `${badCreds.length} with credentials rejected`}; fix under Configuration → Providers`
                  : phone
                    ? ' — see Configuration → Providers'
                    : ' — hover for detail'}
              </div>
            ) : null}
            {t && t.fxMissing > 0 ? (
              <div
                className="form-warn"
                title="No USD reference rate was available for these non-USD rows yet, so they are EXCLUDED from every USD figure (never counted at face value) - revenue is understated by their true value until the rate lands. Rates refresh daily; see the source's warning under Settings → Providers."
              >
                {t.fxMissing} row{t.fxMissing > 1 ? 's' : ''} excluded — no USD rate yet
              </div>
            ) : null}
            {t && t.costUnknown > 0 ? (
              <div
                className="form-warn"
                title="Airman reported these buy-side rows without a readable cost, so they count as $0 in every cost figure until a sync re-reads them - cost is understated, not zero."
              >
                {t.costUnknown} Airman row{t.costUnknown > 1 ? 's' : ''} with unknown cost in the
                window (every destination, whatever the filter) — counted as $0
              </div>
            ) : null}
          </div>
        </div>
      </div>

      <div className="filters">
        <div className="seg" role="group" aria-label="Date range" ref={segRef}>
          {RANGES.map((r) => (
            <button key={r.days} aria-pressed={days === r.days} onClick={() => setDays(r.days)}>
              {r.label}
            </button>
          ))}
        </div>
        {phone ? (
          <>
            <button
              type="button"
              className="chip"
              aria-expanded={filtersOpen}
              aria-controls="filters-panel"
              onClick={() => setFiltersOpen((v) => !v)}
            >
              Filters{activeFilters.length ? ` · ${activeFilters.length}` : ''}
            </button>
            {!filtersOpen
              ? activeFilters.map(([k, v]) => (
                  <button
                    key={k}
                    type="button"
                    className="chip active-filter"
                    title="Clear this filter"
                    onClick={() => setFilters((f) => ({ ...f, [k]: undefined }))}
                  >
                    {filterChipLabel(k, v)} ✕
                  </button>
                ))
              : null}
          </>
        ) : null}
        {!phone || filtersOpen ? (
          <div className="filter-group" id="filters-panel">
            <FilterSelect
              label="All accounts"
              value={filters.account}
              options={(s?.accounts ?? []).map((a) => ({ value: a.id, label: accountOption(a) }))}
              onChange={(account) => setFilters((f) => ({ ...f, account }))}
            />
            <FilterSelect
              label="All reps"
              value={filters.rep}
              options={(s?.filterOptions.reps ?? []).map((v) => ({ value: v, label: v }))}
              onChange={(rep) => setFilters((f) => ({ ...f, rep }))}
            />
            <FilterSelect
              label="All sources"
              value={filters.provider}
              options={(s?.filterOptions.providers ?? []).map((v) => ({
                value: v,
                label: provDisp(v),
              }))}
              onChange={(provider) => setFilters((f) => ({ ...f, provider }))}
            />
            <FilterSelect
              label="All feeds"
              value={filters.feedId}
              options={(s?.filterOptions.feeds ?? []).map((v) => ({
                value: v,
                label: `Feed ${v}`,
              }))}
              onChange={(feedId) => setFilters((f) => ({ ...f, feedId }))}
            />
            <FilterSelect
              label="All countries"
              value={filters.country}
              options={(s?.filterOptions.countries ?? []).map((v) => ({ value: v, label: v }))}
              onChange={(country) => setFilters((f) => ({ ...f, country }))}
            />
            <button
              className="chip overview-toggle"
              aria-pressed={overview}
              title="Fold the leakage list and the charts away so the network table sits under the tiles"
              onClick={toggleOverview}
            >
              {overview ? 'Collapse overview' : 'Show overview (leakage + charts)'}
            </button>
          </div>
        ) : null}
      </div>

      {summary.isError ? (
        <div className="warnbox">
          Could not load the summary — showing nothing rather than stale guesses. Retry via the
          range buttons.
        </div>
      ) : null}

      {/* ------------------------------------------------------------ tiles -- */}
      {t ? (
        <>
          {/* p: the equal-length window before this one; null for a single day. */}
          {showCosts ? (
            <div className="tiles">
              {/* The hero is the OPERATING net once the P&L has loaded - the
                  top-down figure, independent of matching - with the MATCHED
                  layer as its sub-line (matched net on linked revenue, its
                  margin, the linked feed count; P2e) on BOTH bases (R31).
                  Until the P&L lands the attributed profit stands in, and
                  says so. */}
              {pl.data ? (
                (() => {
                  // Unfiltered the hero is the operating net; under a filter it
                  // is the matched net for that slice, because the operating
                  // one cannot be sliced (lib/pl-view.ts heroView). Before, the
                  // number sat unchanged beside a filtered Revenue tile.
                  const hero = heroView({
                    filtered: activeFilters.length > 0,
                    pl: pl.data,
                    totals: {
                      revenue: t.revenue,
                      profit: t.profit,
                      cost: t.cost,
                      costUnknown: t.costUnknown,
                    },
                    prior: p,
                    what:
                      activeFilters.length === 1
                        ? filterChipLabel(activeFilters[0]![0], activeFilters[0]![1])
                        : null,
                    countryFiltered: !!filters.country,
                  });
                  const window = days > 0 ? `${days}d` : days === 0 ? 'all time' : 'day';
                  return (
                    <Tile
                      hero
                      label={phone ? `${hero.label} · ${window}` : hero.label}
                      value={hero.value != null ? fmtMoney(hero.value) : '—'}
                      neg={(hero.value ?? 0) < 0}
                      sub={
                        phone ? (
                          <>
                            {hero.subShort ? `${hero.subShort} · ` : ''}
                            full P&amp;L on{' '}
                            <a
                              href="/costs"
                              onClick={(e) => {
                                e.preventDefault();
                                navigate?.('/costs');
                              }}
                            >
                              Costs
                            </a>
                          </>
                        ) : (
                          hero.sub
                        )
                      }
                      delta={
                        hero.prior != null && hero.value != null
                          ? chip(hero.value, hero.prior, 'up-good', fmtMoney)
                          : null
                      }
                    />
                  );
                })()
              ) : (
                <Tile
                  hero
                  label="Profit"
                  value={t.profit != null ? fmtMoney(t.profit) : '—'}
                  neg={(t.profit ?? 0) < 0}
                  sub={
                    (t.profit == null
                      ? filters.country
                        ? 'cost unknown for this country on some days (no per-country buy-side rows or counts)'
                        : 'no confirmed buy-side links in range'
                      : t.revenue > 0
                        ? fmtPct((100 * t.profit) / t.revenue) + ' margin'
                        : 'no revenue in range') +
                    ' · attributed only (confirmed links); the operating net is in the P&L below'
                  }
                  delta={p && chip(t.profit, p.profit, 'up-good', fmtMoney)}
                />
              )}
              {(() => {
                // Real money only: the estimate for missing stats is named in
                // the sub-line and never inside the value (lib/costs-view.ts).
                const lt = leakageTile(leak);
                return (
                  <button
                    type="button"
                    className="tile leak"
                    title={
                      phone
                        ? leakOpen
                          ? 'Hide the leakage list'
                          : 'Show the leakage list'
                        : 'Jump to the leakage list'
                    }
                    aria-expanded={phone ? leakOpen : undefined}
                    aria-controls={phone ? 'leakage-inline' : undefined}
                    onClick={() => {
                      if (phone) {
                        setLeakOpen((v) => !v);
                        return;
                      }
                      if (!overview) toggleOverview();
                      setTimeout(() => jumpTo(document.getElementById('leakage')), 50);
                    }}
                  >
                    <div className="head">
                      <div className="label">Leakage at stake</div>
                      <span>
                        {lt.count ? <span className="pill bad">{lt.count}</span> : null}
                        {phone ? <span className="chev">{leakOpen ? '▴' : '▾'}</span> : null}
                      </span>
                    </div>
                    <div className="value neg">{lt.value}</div>
                    <div className="sub">{lt.sub}</div>
                  </button>
                );
              })()}
              {phone && leakOpen ? (
                <div className="leak-inline" id="leakage-inline">
                  {leakage.isError ? (
                    <div className="warnbox">Could not load the leakage list.</div>
                  ) : null}
                  {leakEmpty}
                  {leakRows}
                </div>
              ) : null}
              <Tile
                label="Revenue"
                value={fmtMoney(t.revenue)}
                sub={revenueSub(t)}
                delta={p && chip(t.revenue, p.revenue, 'up-good', fmtMoney)}
              />
              {/* Once the P&L has loaded and no filter is set, the Cost tile is
                  the OPERATING cost (bill + hosting + ingested when real) with
                  the attributed slice named in the sub-line (P2e); the delta
                  chip then has nothing like-for-like to compare against (the
                  previous window's P&L is not fetched), so there is none. */}
              {(() => {
                const view = costTileView({
                  pl: pl.data,
                  tCost: t.cost,
                  filtered: Object.values(filters).some(Boolean),
                  launchCost: t.launchCost,
                  launchCostPer1k: s?.launchCostPer1k,
                });
                return (
                  <Tile
                    label="Cost"
                    value={view.value}
                    sub={view.sub}
                    delta={
                      p && !view.usesOperating ? chip(t.cost, p.cost, 'up-bad', fmtMoney) : null
                    }
                  />
                );
              })()}
            </div>
          ) : (
            <div className="tiles">
              <Tile
                hero
                label="Revenue"
                value={fmtMoney(t.revenue)}
                sub={revenueSub(t)}
                delta={p && chip(t.revenue, p.revenue, 'up-good', fmtMoney)}
              />
            </div>
          )}
          <div className="tiles">
            <Tile
              label="Requests"
              value={fmtInt(t.requests)}
              sub={requestsSub(t)}
              delta={p && chip(t.requests, p.requests, 'neutral', fmtInt)}
            />
            <Tile
              label="Searches"
              value={fmtInt(t.searches)}
              sub={searchesSub(t, s?.daily ?? [])}
              delta={p && chip(t.searches, p.searches, 'up-good', fmtInt)}
            />
            <Tile
              label="Monetized"
              value={fmtInt(t.monetizedSearches)}
              sub={monetizedSub(t)}
              delta={p && chip(t.monetizedSearches, p.monetizedSearches, 'neutral', fmtInt)}
            />
            <Tile
              label="Coverage"
              value={t.coverage != null ? t.coverage.toFixed(1) + '%' : '—'}
              sub={coverageLabel(t.coverageSource, t.coverageCalculatedRows)}
              delta={p && pointsChip(t.coverage, p.coverage)}
            />
            <Tile
              label="Clicks"
              value={fmtInt(t.paidClicks)}
              sub={
                t.monetizedSearches > 0
                  ? fmtPct((100 * t.paidClicks) / t.monetizedSearches) + ' CTR on monetized'
                  : ''
              }
              delta={p && chip(t.paidClicks, p.paidClicks, 'neutral', fmtInt)}
            />
          </div>
        </>
      ) : null}

      {/* ----------------------------------------------- overview: leakage + charts - */}
      {overview ? (
        <>
          {phone ? null : (
            <div className="table-card leak-list" id="leakage">
              <div className="table-head">
                <h2>Leakage — ranked by $ at stake</h2>
                <span className="count">
                  {leakage.data ? `${leakage.data.since} → ${leakage.data.upto}` : ''} · spend with
                  no revenue · unconfirmed links · missing stats · bill nobody owns
                </span>
              </div>
              {leakage.isError ? (
                <div className="warnbox">Could not load the leakage list.</div>
              ) : null}
              {leakEmpty}
              {leakRows}
              <div className="table-note">
                Rules: confirmed spend with $0 revenue for 2+ consecutive crawled days · any spend
                on a link that is not confirmed · a crawlable source with 2+ days it has not crawled
                through (an <b>estimate</b>, never counted) · the launcher bill Airman&apos;s
                allocation does not explain. Every real item is already inside the operating net
                above: this list says where it leaks, it adds nothing on top.
              </div>
            </div>
          )}

          <div className="charts">
            <div className="chart-card">
              <div className="card-head">
                <h2>Revenue &amp; cost by day</h2>
                <div className="legend">
                  {[...providerColors.entries()].map(([p, c]) => (
                    <span className="key" key={p}>
                      <span className="sw" style={{ background: c }} />
                      {provDisp(p)}
                    </span>
                  ))}
                  <span className="key">
                    <span className="sw" style={{ background: 'var(--gold)' }} />
                    cost
                  </span>
                </div>
              </div>
              <BarChart data={revenueData} mode="stack" formatValue={(v) => '$' + Math.round(v)} />
            </div>
            <div className="chart-card">
              <div className="card-head">
                <h2>Visits sent vs searches by day</h2>
                <div className="legend">
                  <span className="key">
                    <span className="sw" style={{ background: 'var(--bar-dim)' }} />
                    Searches (total)
                  </span>
                  <span className="key">
                    <span className="sw" style={{ background: 'var(--primary)', height: 2 }} />
                    Visits sent
                  </span>
                </div>
              </div>
              <BarChart data={visitsData} mode="stack" formatValue={(v) => fmtInt(v)} />
              <div className="table-note">
                The line is every visit the buy side sent (all Airman destinations, whatever the
                account, rep, provider or feed filter); the bars are all searches reported back. The
                gap is traffic that never became a search - the first place leakage shows.
              </div>
            </div>
          </div>
        </>
      ) : null}

      {/* -------------------------------------------------------- breakdowns - */}
      <div className="breakdowns">
        <Breakdown
          title="Revenue by rep — click to filter"
          items={s?.byRep ?? []}
          state={bdState}
          lastData={s?.lastUpdated}
          active={filters.rep}
          onSelect={(k) => setFilters((f) => ({ ...f, rep: f.rep === k ? undefined : k }))}
        />
        <Breakdown
          title="Revenue by provider — click to filter"
          items={(s?.byProvider ?? []).map((i) => ({
            ...i,
            key: provDisp(i.key, i.label),
            raw: i.key,
          }))}
          state={bdState}
          lastData={s?.lastUpdated}
          active={filters.provider}
          onSelect={(k) =>
            setFilters((f) => ({ ...f, provider: f.provider === k ? undefined : k }))
          }
        />
        <Breakdown
          title="Revenue by feed"
          items={(s?.byFeed.items ?? []).map((i) => ({
            ...i,
            key: `Feed ${i.key}${i.label ? ` · ${i.label}` : ''}`,
          }))}
          total={s?.byFeed.total}
          state={bdState}
          lastData={s?.lastUpdated}
        />
        <Breakdown
          title="Revenue by geo — click to filter"
          items={s?.byGeo.items ?? []}
          total={s?.byGeo.total}
          state={bdState}
          lastData={s?.lastUpdated}
          active={filters.country}
          onSelect={(k) => setFilters((f) => ({ ...f, country: f.country === k ? undefined : k }))}
        />
      </div>

      {/* ------------------------------------------------------ profit table - */}
      {phone ? (
        <FeedCards
          feeds={feedRows}
          rowSource={rowSource}
          onRow={(f) => setDrill(`${f.accountId}:${f.feedId}`)}
          operatingNet={
            !Object.values(filters).some(Boolean) && pl.data ? pl.data.operating.net : null
          }
        />
      ) : (
        <div className="table-card costcol">
          {typeError ? <div className="warnbox">{typeError}</div> : null}
          <div className="table-head">
            <h2>The network — Profit by feed</h2>
            <span className="count">
              {feedRows.length !== feedTotal ? `${feedRows.length} of ${feedTotal}` : feedTotal}{' '}
              feeds · click a row to drill down
            </span>
          </div>
          <ProfitTable
            feeds={feedRows}
            typeOptions={s?.feedTypeOptions ?? []}
            rowSource={rowSource}
            onType={(v) => setFeedType.mutate(v)}
            onRow={(f) => setDrill(`${f.accountId}:${f.feedId}`)}
            operatingNet={
              !Object.values(filters).some(Boolean) && pl.data ? pl.data.operating.net : null
            }
            note={
              <>
                {geoNote ? `${geoNote} ` : ''}
                Revenue from feed stats; cost is the buy side’s attributed spend flowing through{' '}
                <b>confirmed</b> destination↔feed links (window-aware, read-time). Suggested matches
                show their would-be cost but never count toward profit until confirmed on the
                Matching page. Cost marked * includes days the buy side reported as incomplete — it
                may still move.{' '}
                {pl.data?.costBasis === 'undetect'
                  ? 'The modelled launch cost is retired: the launcher bill (Undetect) is the control total in the P&L below, and per-feed profit is revenue minus the buy side’s attributed spend only.'
                  : s && s.launchCostPer1k > 0
                    ? `Launch is a legacy modelled acquisition cost ($${s.launchCostPer1k.toFixed(2)} per 1,000 committed visits — LAUNCH_COST_PER_1K, normally 0) on the same confirmed-link visits; profit is revenue minus both.`
                    : 'The launch model (LAUNCH_COST_PER_1K, normally 0) is retired; connect Undetect under Configuration → Buy side for the launcher bill to become the control total.'}
                {/* Only when there IS a hosting line below: with Civo unconnected the
                  P&L card has no Hosting row for this sentence to point at. */}
                {pl.data?.hosting
                  ? ' Hosting is an overhead in the P&L below and never reaches a feed.'
                  : ''}
                {pl.data?.ingestedCost?.semantics === 'real'
                  ? ' Ingested is the per-row traffic, compute and LLM cost on the feed, confirmed as real under Configuration → Buy side.'
                  : ''}
              </>
            }
          />
        </div>
      )}

      {/* ------------------------------------------------------ daily page - */}
      <div className="table-card daily-link-card">
        <div className="table-head">
          <h2>Daily breakdown</h2>
        </div>
        <p className="table-note">
          Moved to its own page: the daily stats per account, feed and geo, grouped by day. It opens on this
          range and these filters.
        </p>
        <a
          className="ghost small"
          href={`/daily${dailySearch({ days, filters })}`}
          onClick={(e) => {
            if (!navigate) return;
            e.preventDefault();
            navigate(`/daily${dailySearch({ days, filters })}`);
          }}
        >
          Open the daily breakdown →
        </a>
      </div>

      <FeedDrawer
        target={drill}
        feeds={feedRows}
        leaks={leak?.items ?? []}
        rowSource={rowSource}
        onClose={() => setDrill(null)}
        onFilter={(feedId) => {
          setFilters((f) => ({ ...f, feedId }));
          setDrill(null);
        }}
        onMatching={(feedId) => navigate?.(`/matching#feed-${encodeURIComponent(feedId)}`)}
      />
    </div>
  );
}

/** One leakage row: the amount (an estimate in muted grey, never red), the kind, the words, the age, the action. */
function LeakageRow({ item, onAct }: { item: LeakageItem; onAct: (i: LeakageItem) => void }) {
  const tag = leakageTag(item.kind);
  const age = item.days ? `${item.days} day${item.days === 1 ? '' : 's'}` : '';
  return (
    <div className="leak-row" data-leak={item.kind}>
      <span
        className={'amt' + (item.estimate ? ' est' : '')}
        title={
          item.estimate
            ? 'an estimate - not counted in the total'
            : `already inside the operating net (${item.plLine})`
        }
      >
        {item.estimate ? '~' : ''}
        {fmtMoney(item.amount)}
      </span>
      <span className={tag.cls}>{tag.text}</span>
      <span className="desc">
        <b>{item.label}</b>: {item.description}
      </span>
      <span className="age">{age}</span>
      <button type="button" className="ghost small" onClick={() => onAct(item)}>
        {leakageAction(item)}
      </button>
    </div>
  );
}

/**
 * The drill-down for one feed: its window P&L, the leakage on it, its link
 * status, and the two ways out - the Matching page (deep-linked to its card)
 * or the page filtered to it.
 */
function FeedDrawer(props: {
  target: string | null;
  feeds: ProfitRow[];
  leaks: LeakageItem[];
  rowSource: (r: {
    accountId: string;
    provider: string;
    providerLabel?: string | null | undefined;
  }) => string;
  onClose: () => void;
  onFilter: (feedId: string) => void;
  onMatching: (feedId: string) => void;
}) {
  const f = props.target
    ? props.feeds.find((r) => `${r.accountId}:${r.feedId}` === props.target)
    : undefined;
  const leaks = f
    ? props.leaks.filter((l) => l.feedId === f.feedId && l.accountId === f.accountId)
    : [];
  const net = f?.profit ?? null;
  const status = !f
    ? ''
    : f.buyCost != null
      ? f.linksConfirmed
        ? 'confirmed link'
        : 'partly confirmed'
      : f.suggestedCost != null
        ? 'suggested link — cost not counted'
        : 'revenue only — no link';
  return (
    <Modal
      open={!!f}
      title={f ? `Feed ${f.feedId}` : ''}
      description={
        f
          ? `${f.accountId} · ${f.provider ? props.rowSource({ accountId: f.accountId, provider: f.provider }) : '—'} · ${f.rep ?? '—'} · ${status}`
          : ''
      }
      onClose={props.onClose}
      actions={
        f ? (
          <>
            <button
              type="button"
              className="ui-btn ui-btn--ghost ui-btn--sm"
              onClick={() => props.onFilter(f.feedId)}
            >
              Filter page to this feed
            </button>
            <button
              type="button"
              className="ui-btn ui-btn--primary ui-btn--sm"
              onClick={() => props.onMatching(f.feedId)}
            >
              Open in Matching
            </button>
          </>
        ) : null
      }
    >
      {f ? (
        <div className="drill">
          {f.investigate ? (
            <div className="fc-pills">
              <span
                className="pill bad"
                title="over 90% margin on a confirmed link - a red flag, not a win"
              >
                investigate
              </span>
            </div>
          ) : null}
          <h3>This window&apos;s P&amp;L</h3>
          <dl>
            <dt>Visits sent (committed)</dt>
            <dd>{f.buyCommitted ? fmtInt(f.buyCommitted) : '—'}</dd>
            <dt>Searches back</dt>
            <dd>{fmtInt(f.searches)}</dd>
            <dt>Revenue</dt>
            <dd>
              {fmtMoney(f.revenue)}
              <Excluded n={f.fxMissing} />
            </dd>
            <dt>Attributed cost</dt>
            <dd>
              {f.buyCost != null
                ? fmtMoney(f.buyCost) + (f.costIncomplete ? '*' : '')
                : f.suggestedCost != null
                  ? `(${fmtMoney(f.suggestedCost)}) not counted`
                  : '— no confirmed link'}
            </dd>
            <dt>Net</dt>
            <dd className={'net' + (net != null && net < 0 ? ' neg' : '')}>
              {net != null ? fmtMoney(net) : '—'}
            </dd>
            <dt>Margin</dt>
            <dd>{f.margin != null ? fmtPct(f.margin) : '—'}</dd>
          </dl>
          <h3>Leakage on this feed</h3>
          {leaks.length ? (
            leaks.map((l) => (
              <div className="leak-row" key={`${l.kind}:${l.label}`}>
                <span className={'amt' + (l.estimate ? ' est' : '')}>
                  {l.estimate ? '~' : ''}
                  {fmtMoney(l.amount)}
                </span>
                <span className="desc">{l.description}</span>
              </div>
            ))
          ) : (
            <div className="table-note">
              None — every day in the window with confirmed spend earned something.
            </div>
          )}
        </div>
      ) : null}
    </Modal>
  );
}

/**
 * Phone (2026-09-14 mobile design): the network as one card per feed, tap to
 * drill down. The same rows and the same rules as the table - a suggested
 * link's cost is shown in brackets and never counted, revenue-only feeds
 * have no net - and the cards' own sum is said apart from the operating net,
 * never as its total (rule 8).
 */
function FeedCards(props: {
  feeds: ProfitRow[];
  rowSource: (r: {
    accountId: string;
    provider: string;
    providerLabel?: string | null | undefined;
  }) => string;
  onRow: (f: ProfitRow) => void;
  operatingNet: number | null;
}) {
  const linked = props.feeds.filter((f) => f.buyCost != null);
  const sumRevenue = props.feeds.reduce((t, f) => t + f.revenue, 0);
  const sumCost = linked.reduce((t, f) => t + (f.buyCost ?? 0), 0);
  const sumNet = linked.reduce((t, f) => t + (f.profit ?? 0), 0);
  return (
    <>
      <div className="section-head">
        <h2>The network</h2>
        <span className="count">
          {props.feeds.length} feed{props.feeds.length === 1 ? '' : 's'} · tap to drill down
        </span>
      </div>
      <div className="feed-cards">
        {props.feeds.map((f) => {
          const status =
            f.buyCost != null
              ? f.linksConfirmed
                ? null
                : { text: 'partly confirmed', cls: 'pill gold' }
              : f.suggestedCost != null
                ? { text: 'suggested — not counted', cls: 'pill gold' }
                : { text: 'revenue only — no link', cls: 'pill' };
          const net = f.profit;
          return (
            <button
              type="button"
              className="feed-card"
              key={`${f.accountId}:${f.feedId}`}
              data-feed={f.feedId}
              onClick={() => props.onRow(f)}
            >
              <div className="fc-head">
                <span className="fc-name">
                  Feed {f.feedId}{' '}
                  <span className="count">
                    {f.provider
                      ? props.rowSource({ accountId: f.accountId, provider: f.provider })
                      : '—'}{' '}
                    · {f.rep ?? '—'}
                  </span>
                </span>
                <span className={'fc-net' + (net == null ? ' none' : net < 0 ? ' neg' : '')}>
                  {net != null ? fmtMoney(net) : '—'}
                </span>
              </div>
              <div className="fc-line">
                <span>
                  {fmtMoney(f.revenue)}
                  <Excluded n={f.fxMissing} /> rev ·{' '}
                  {f.buyCost != null
                    ? fmtMoney(f.buyCost) + (f.costIncomplete ? '*' : '')
                    : f.suggestedCost != null
                      ? `(${fmtMoney(f.suggestedCost)})`
                      : '—'}{' '}
                  cost
                </span>
                <span className="fc-margin">
                  {f.margin != null ? fmtPct(f.margin) + ' margin' : 'no confirmed link'}
                </span>
              </div>
              <div className="fc-bar">
                <span
                  className={f.investigate ? 'hot' : ''}
                  style={{
                    width: `${f.margin != null ? Math.max(0, Math.min(f.margin, 100)) : 0}%`,
                  }}
                />
              </div>
              {status || f.investigate ? (
                <div className="fc-pills">
                  {status ? <span className={status.cls}>{status.text}</span> : null}
                  {f.investigate ? (
                    <span
                      className="pill bad"
                      title="over 90% margin on a confirmed link - a red flag, not a win"
                    >
                      investigate
                    </span>
                  ) : null}
                </div>
              ) : null}
            </button>
          );
        })}
      </div>
      <div className="network-total">
        The cards add up to {fmtMoney(sumRevenue)} revenue, {fmtMoney(sumCost)} attributed cost,{' '}
        {fmtMoney(sumNet)} net on the linked feeds.
        {props.operatingNet != null ? (
          <>
            {' '}
            The operating net for the window is {fmtMoney(props.operatingNet)} — the layers are on
            the Costs tab.
          </>
        ) : (
          ' The operating net is not shown under a filter: overheads and unclaimed spend cannot be sliced by rep, source or feed.'
        )}{' '}
        A cost in brackets is a suggested link&apos;s and is never counted; * marks a cost that may
        still move.
      </div>
    </>
  );
}

function Tile(props: {
  label: string;
  value: string;
  sub?: ReactNode;
  hero?: boolean;
  neg?: boolean;
  delta?: Chip | null;
}) {
  return (
    <div className={'tile' + (props.hero ? ' hero' : '')}>
      <div className="head">
        <div className="label">{props.label}</div>
        {props.delta ? (
          <span className={'chip ' + props.delta.cls} title={props.delta.title}>
            {props.delta.text}
          </span>
        ) : null}
      </div>
      <div className={'value' + (props.neg ? ' neg' : '')}>{props.value}</div>
      {props.sub ? <div className="sub">{props.sub}</div> : null}
    </div>
  );
}

type BreakdownState = 'loading' | 'error' | 'ready';

function Breakdown(props: {
  title: string;
  items: { key: string; revenue: number; raw?: string | undefined }[];
  total?: number | undefined;
  state: BreakdownState;
  /** the newest stats timestamp the summary knows of - named when a window is empty. */
  lastData?: string | null | undefined;
  /** the filter value currently applied on this axis, if any. */
  active?: string | undefined;
  onSelect?: ((key: string) => void) | undefined;
}) {
  const max = Math.max(1e-9, ...props.items.map((i) => i.revenue));
  return (
    <div className="bd">
      <div className="bd-head">
        <h2>{props.title}</h2>
        <span className="count">
          {props.total != null && props.total > props.items.length
            ? `top ${props.items.length} of ${props.total}`
            : props.items.length}
        </span>
      </div>
      {props.items.length === 0 ? (
        <div className="bd-empty">
          {props.state === 'loading'
            ? 'loading…'
            : props.state === 'error'
              ? 'could not load the summary'
              : `no revenue rows in this window${props.lastData ? ` · last update ${props.lastData.slice(0, 10)}` : ''}`}
        </div>
      ) : null}
      {props.items.map((i) => (
        <div
          className={
            'bd-row' +
            (props.onSelect ? ' clickable' : '') +
            (props.active != null && props.active === (i.raw ?? i.key) ? ' active' : '')
          }
          key={i.key}
          role={props.onSelect ? 'button' : undefined}
          tabIndex={props.onSelect ? 0 : undefined}
          title={props.onSelect ? 'Filter the page to this' : undefined}
          onClick={props.onSelect ? () => props.onSelect!(i.raw ?? i.key) : undefined}
          onKeyDown={
            props.onSelect
              ? (e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    props.onSelect!(i.raw ?? i.key);
                  }
                }
              : undefined
          }
        >
          <span className="name">{i.key}</span>
          <span className="val">{fmtMoney(i.revenue)}</span>
          <span className="bd-bar">
            <span style={{ width: `${(100 * i.revenue) / max}%` }} />
          </span>
        </div>
      ))}
    </div>
  );
}

function ProfitTable(props: {
  feeds: ProfitRow[];
  typeOptions: string[];
  rowSource: (r: {
    accountId: string;
    provider: string;
    providerLabel?: string | null | undefined;
  }) => string;
  onType: (v: { accountId: string; feedId: string; type: string }) => void;
  note: ReactNode;
  onRow?: ((f: ProfitRow) => void) | undefined;
  /** the window's operating net when no filter is on (shown apart from the rows' sum, never as their footer). */
  operatingNet?: number | null | undefined;
}) {
  const linked = props.feeds.filter((f) => f.buyCost != null);
  // The rows' own sums: revenue over every row, cost and net over the linked
  // rows (a suggested cost is never counted). The operating net is a
  // different number - overheads and unclaimed spend never reach a feed - so
  // it is said beside the sum, never printed as its total (rule 8).
  const sumRevenue = props.feeds.reduce((t, f) => t + f.revenue, 0);
  const sumCost = linked.reduce((t, f) => t + (f.buyCost ?? 0), 0);
  const sumNet = linked.reduce((t, f) => t + (f.profit ?? 0), 0);
  const suggested = props.feeds.filter((f) => f.buyCost == null && f.suggestedCost != null);
  const revenueOnly = props.feeds.filter((f) => f.buyCost == null && f.suggestedCost == null);

  // Feed, Type and Basis are the row's identity and never hide; the money
  // and volume columns hide when empty on every row - Launch first, now that
  // the model is retired and renders $0.00 on every linked row (R33).
  const cols: Col<ProfitRow>[] = [
    {
      key: 'Feed',
      head: 'l',
      fixed: true,
      get: (f) => f.feedId,
      cellClass: 'l',
      cell: (f) => (
        <>
          {f.feedId} <span className="count">({f.accountId})</span>
        </>
      ),
    },
    {
      key: 'Type',
      head: 'l',
      fixed: true,
      get: (f) => f.type,
      cellClass: 'l',
      cell: (f) => (
        <select
          className="type-select"
          value={f.type === 'Unclassified' ? '' : f.type}
          onChange={(e) =>
            props.onType({ accountId: f.accountId, feedId: f.feedId, type: e.target.value })
          }
          title="Classify this account's feed"
        >
          <option value="">Unclassified</option>
          {props.typeOptions.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
      ),
    },
    { key: 'Rep', head: 'l', get: (f) => f.rep, cellClass: 'l', cell: (f) => f.rep ?? '—' },
    {
      key: 'Provider',
      head: 'l',
      get: (f) => f.provider,
      cellClass: 'l',
      cell: (f) =>
        f.provider ? props.rowSource({ accountId: f.accountId, provider: f.provider }) : '—',
    },
    {
      key: 'Committed (buy)',
      get: (f) => f.buyCommitted,
      cell: (f) => (f.buyCommitted ? fmtInt(f.buyCommitted) : '—'),
    },
    { key: 'Searches', get: (f) => f.searches, cell: (f) => fmtInt(f.searches) },
    {
      key: 'Revenue',
      // An all-excluded feed still has something to say (the exclusion), so it keeps the column.
      get: (f) => (f.fxMissing > 0 ? 'excluded' : f.revenue),
      cellClass: 'pay',
      cell: (f) => (
        <>
          {fmtMoney(f.revenue)}
          <Excluded n={f.fxMissing} />
        </>
      ),
    },
    {
      key: 'Buy cost',
      get: (f) => f.buyCost ?? f.suggestedCost,
      cellClass: 'dim',
      cell: (f) =>
        f.buyCost != null
          ? fmtMoney(f.buyCost) + (f.costIncomplete ? '*' : '')
          : f.suggestedCost != null
            ? `(${fmtMoney(f.suggestedCost)})`
            : '—',
    },
    {
      key: 'Launch',
      get: (f) => f.launchCost,
      cellClass: 'dim',
      cell: (f) => (f.launchCost != null ? fmtMoney(f.launchCost) : '—'),
    },
    // Null on every row until the owner confirms the ingested columns as real
    // (P7), so the rule hides it and the note names it.
    {
      key: 'Ingested',
      get: (f) => f.ingestedCost,
      cellClass: 'dim',
      cell: (f) => (f.ingestedCost != null ? fmtMoney(f.ingestedCost) : '—'),
    },
    {
      key: 'Profit',
      get: (f) => f.profit,
      cellClass: (f) => (f.profit != null && f.profit < 0 ? 'neg' : ''),
      cell: (f) => (f.profit != null ? fmtMoney(f.profit) : '—'),
    },
    {
      key: 'Margin',
      get: (f) => f.margin,
      cell: (f) => (f.margin != null ? fmtPct(f.margin) : '—'),
    },
    {
      key: 'Basis',
      head: 'l',
      fixed: true,
      get: (f) => f.linksConfirmed,
      cellClass: 'l',
      cell: (f) => (
        <>
          {f.investigate ? (
            <span
              className="pill warn"
              title="Over 90% margin on a feed that carries cost: in this data that is almost always a clipped link window or a missing pull, not a win"
            >
              investigate
            </span>
          ) : null}{' '}
          {f.buyCost != null ? (
            <span className={'pill ' + (f.linksConfirmed ? 'ok' : 'gold')}>
              {f.linksConfirmed ? 'confirmed' : 'partly confirmed'}
            </span>
          ) : f.suggestedCost != null ? (
            <span className="pill gold">suggested — not counted</span>
          ) : (
            <span className="pill">revenue only</span>
          )}
        </>
      ),
    },
  ];
  const { shown, hidden } = visibleColumns(props.feeds, cols);

  const section = (label: string) => (
    <tr className="section" key={label}>
      <td colSpan={shown.length}>{label}</td>
    </tr>
  );

  const row = (f: ProfitRow) => (
    <tr
      key={`${f.accountId}:${f.feedId}`}
      className={props.onRow ? 'clickable' : undefined}
      data-feed={f.feedId}
      title={props.onRow ? 'Open the drill-down' : undefined}
      onClick={
        props.onRow
          ? (e) => {
              if (!(e.target instanceof HTMLElement && e.target.closest('select, button, a')))
                props.onRow!(f);
            }
          : undefined
      }
    >
      {shown.map((c) => (
        <td key={c.key} className={tdClass(c, f)}>
          {c.cell(f)}
        </td>
      ))}
    </tr>
  );

  return (
    <>
      <div className="table-scroll">
        <table className="stat-table">
          <thead>
            <tr>
              {shown.map((c) => (
                <th key={c.key} className={c.head}>
                  {c.key}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {linked.length ? section('Linked to buy side — real cost') : null}
            {linked.map(row)}
            {suggested.length
              ? section('Suggested matches — cost NOT counted until confirmed')
              : null}
            {suggested.map(row)}
            {revenueOnly.length ? section('No linked destination — revenue only') : null}
            {revenueOnly.map(row)}
          </tbody>
        </table>
      </div>
      <div className="network-total">
        <b>The rows add up to</b> {fmtMoney(sumRevenue)} revenue, {fmtMoney(sumCost)} attributed
        cost, {fmtMoney(sumNet)} net on the linked feeds.
        {props.operatingNet != null ? (
          <>
            {' '}
            The <b>operating net</b> for the window is {fmtMoney(props.operatingNet)} — it also
            carries the spend no link claims, the launcher bill&apos;s residual, hosting and other
            costs, which never reach a feed; the layers are on the Costs tab.
          </>
        ) : (
          ' The operating net is not shown under a filter: overheads and unclaimed spend cannot be sliced by rep, source or feed.'
        )}
      </div>
      <div className="table-note">
        {props.note}
        {hidden.length ? ` ${hiddenColumnsNote(hidden)}` : ''}
      </div>
    </>
  );
}

/**
 * The P&L, four layers. Operating is the headline - computed top-down from
 * the sources, so a late link window cannot distort it. Matched is the
 * detail with its coverage shown, so partial matching cannot masquerade as a
 * full P&L. Matching gaps is what no link claims yet (its net moves as links
 * are confirmed); Overheads is what no feed can ever own (the bill beyond
 * Airman, hosting, other fixed costs, the ingested cost once confirmed real).
 * Operating = Matched + Matching gaps + Overheads, and the older Unattributed
 * (= Matching gaps + Overheads) still ties; the note says so, or by how much
 * it does not (P8).
 *
 * The coverage header at the top says how much of the window each figure
 * covers - a 30-day net on 12 crawled days must never read as a 30-day net.
 * The P7 banner names the ingested cost columns until the owner has decided
 * what they are. "Download statement" (admin) fetches the month's CSV as a
 * blob with the session, never through a bare link (P12).
 */
